/**
 * RoiCalculatorButton — the entry control for the ROI calculator, rendered inside the selected
 * site's ScorePanel (placement B: contextual launch, not a standalone top-bar page).
 *
 * T-627: the calculator was gated on isAdmin (Cognito 'admins') while it was an internal surface.
 * It is now part of the product for every signed-in user, so the role check is gone from both this
 * control and the dialog it opens. Access is still bounded by the session: the whole map sits
 * behind RequireAuth, so "everyone" means every authenticated user. Nothing else changes — the
 * calculator is pure client-side compute with no privileged request behind it, so there was never
 * a server-side permission to relax alongside this.
 *
 * Styled to match the panel's other row action (ExportPdfButton).
 */
import { useState } from 'react'
import { Calculator } from 'lucide-react'
import { RoiCalculatorDialog } from './RoiCalculatorDialog'
import { useAuth } from '../../auth/useAuth'

export interface RoiCalculatorButtonProps {
  /** T-478: the selected site's address (searched or map-clicked), passed to the dialog as context. */
  siteAddress?: string | null
  /** T-478: true when `siteAddress` is a map-click reverse-geocoded (approximate) address. */
  addressApproximate?: boolean
}

export function RoiCalculatorButton({
  siteAddress = null,
  addressApproximate = false,
}: RoiCalculatorButtonProps = {}) {
  const [open, setOpen] = useState(false)
  const { user } = useAuth()

  // T-ROI: hide calculator from non-admin users while it's being redesigned.
  // Admins still get the full working calculator for internal use.
  if (!user?.isAdmin) {
    return (
      <span
        className="flex w-full items-center justify-between
                   min-h-[44px] mt-2 border-t border-border/40 pt-2 px-0.5
                   text-xs text-muted-foreground
                   bg-transparent rounded-sm"
      >
        <span>Coming soon — ROI calculator</span>
        <Calculator size={14} aria-hidden="true" />
      </span>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open the ROI calculator"
        className="flex w-full items-center justify-between
                   min-h-[44px] mt-2 border-t border-border/40 pt-2 px-0.5
                   text-xs text-muted-foreground hover:text-primary
                   cursor-pointer bg-transparent outline-none
                   focus-visible:ring-2 focus-visible:ring-ring rounded-sm
                   transition-colors"
      >
        <span>ROI calculator</span>
        <Calculator size={14} aria-hidden="true" />
      </button>
      <RoiCalculatorDialog
        open={open}
        onOpenChange={setOpen}
        siteAddress={siteAddress}
        addressApproximate={addressApproximate}
      />
    </>
  )
}
